import React, { useEffect, useState, useCallback } from 'react';
import { Search, Loader2, Check, X, User, MapPin, Award } from 'lucide-react';
import { toast } from 'sonner';
import adminAxios from './adminAxios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';

const AdminProfessionalApprovals = () => {
  const [regs, setRegs] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, suspended: 0 });
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('status', filter);
      if (search) params.set('search', search);
      const { data } = await adminAxios.get(`/admin/professional-approvals?${params}`);
      setRegs(data.registrations || []);
      setCounts(data.counts || {});
    } catch (err) {
      toast.error('Erro ao carregar cadastros');
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  const approve = async (reg) => {
    if (!window.confirm(`Aprovar ${reg.name}? Uma conta profissional será criada.`)) return;
    try {
      const { data } = await adminAxios.post(`/admin/professional-approvals/${reg.reg_id}/approve`, {});
      toast.success(`Profissional aprovado. E-mail: ${data.user_email}`);
      load();
      setSelected(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao aprovar');
    }
  };

  const reject = async () => {
    try {
      await adminAxios.post(`/admin/professional-approvals/${selected.reg_id}/reject`, { reason: rejectReason });
      toast.success('Cadastro recusado');
      setShowReject(false);
      setRejectReason('');
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao rejeitar');
    }
  };

  const tabs = [
    { id: 'pending', label: `Pendentes (${counts.pending || 0})`, testid: 'tab-pending' },
    { id: 'approved', label: `Aprovados (${counts.approved || 0})`, testid: 'tab-approved' },
    { id: 'rejected', label: `Recusados (${counts.rejected || 0})`, testid: 'tab-rejected' },
    { id: 'all', label: 'Todos', testid: 'tab-all' },
  ];

  return (
    <div className="p-4" data-testid="admin-professional-approvals">
      <h2 className="text-2xl font-black text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
        Aprovação de Profissionais
      </h2>

      <div className="flex flex-wrap gap-2 mb-4">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            data-testid={`prof-approvals-${t.testid}`}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${
              filter === t.id ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="relative mb-4">
        <Search className="w-4 h-4 absolute top-2.5 left-3 text-slate-400" />
        <Input
          placeholder="Buscar por nome, CPF, e-mail ou registro..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="prof-approvals-search"
        />
      </div>

      {loading ? (
        <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600" /></div>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left border-b border-slate-200">
                <th className="py-2 px-3">Nome</th>
                <th>CPF</th>
                <th>Registro</th>
                <th>Categorias</th>
                <th>Status</th>
                <th>Data</th>
                <th className="text-right pr-4">Ações</th>
              </tr>
            </thead>
            <tbody data-testid="prof-approvals-table-body">
              {regs.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-slate-500">Nenhum cadastro nesta categoria</td></tr>
              ) : regs.map((r) => (
                <tr key={r.reg_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-3 font-semibold">{r.name}</td>
                  <td className="text-xs">{r.cpf}</td>
                  <td className="text-xs">{r.registration_number}</td>
                  <td className="text-xs">{(r.categories || []).join(', ')}</td>
                  <td>
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded font-bold ${
                      r.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                      r.status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                      'bg-red-100 text-red-800'
                    }`}>{r.status}</span>
                  </td>
                  <td className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString('pt-BR')}</td>
                  <td className="text-right pr-4">
                    <Button size="sm" variant="outline" onClick={() => setSelected(r)} data-testid={`prof-view-${r.reg_id}`}>Ver</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="prof-detail-modal">
            <div className="p-6">
              <h3 className="text-xl font-black mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>{selected.name}</h3>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <p><strong>CPF:</strong> {selected.cpf}</p>
                  <p><strong>Registro:</strong> {selected.registration_number}</p>
                  <p><strong>E-mail:</strong> {selected.email}</p>
                  <p><strong>Telefone:</strong> {selected.phone}</p>
                </div>
                <p><strong>Formação:</strong> {selected.formation}</p>
                <div>
                  <p className="font-bold text-slate-700 mb-1"><Award className="w-4 h-4 inline mr-1" />Categorias:</p>
                  <div className="flex flex-wrap gap-1">
                    {(selected.categories || []).map((c) => <span key={c} className="bg-teal-100 text-teal-800 text-xs px-2 py-0.5 rounded">{c}</span>)}
                  </div>
                </div>
                <div>
                  <p className="font-bold text-slate-700 mb-1">Especialidades:</p>
                  <div className="flex flex-wrap gap-1">
                    {(selected.specialties || []).map((s) => <span key={s} className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded">{s}</span>)}
                  </div>
                </div>
                <div>
                  <p className="font-bold text-slate-700 mb-1"><MapPin className="w-4 h-4 inline mr-1" />Locais:</p>
                  {(selected.locations || []).map((l, i) => (
                    <div key={i} className="text-xs bg-slate-50 rounded p-2 mb-1">
                      <p><strong>{i + 1}.</strong> {l.place_name ? <span className="text-teal-700 font-semibold">{l.place_name} — </span> : null}{l.address}</p>
                      <p className="text-slate-600">{l.city}/{l.state} • {l.phone}</p>
                    </div>
                  ))}
                </div>
                {selected.rejection_reason && (
                  <div className="bg-red-50 border border-red-200 rounded p-2 text-xs">
                    <strong>Motivo da recusa:</strong> {selected.rejection_reason}
                  </div>
                )}
              </div>

              {selected.status === 'pending' && (
                <div className="flex gap-2 mt-6">
                  <Button onClick={() => approve(selected)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="prof-approve-btn">
                    <Check className="w-4 h-4 mr-1" /> Aprovar
                  </Button>
                  <Button variant="destructive" onClick={() => setShowReject(true)} className="flex-1" data-testid="prof-reject-btn">
                    <X className="w-4 h-4 mr-1" /> Recusar
                  </Button>
                </div>
              )}
              <Button variant="outline" onClick={() => setSelected(null)} className="w-full mt-2">Fechar</Button>
            </div>
          </div>
        </div>
      )}

      {showReject && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6" data-testid="prof-reject-dialog">
            <h4 className="font-bold mb-2">Motivo da recusa</h4>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full h-24 border border-slate-200 rounded p-2 text-sm"
              placeholder="Explique brevemente..."
              data-testid="prof-reject-reason"
            />
            <div className="flex gap-2 mt-3">
              <Button variant="outline" onClick={() => setShowReject(false)} className="flex-1">Cancelar</Button>
              <Button variant="destructive" onClick={reject} className="flex-1" data-testid="prof-reject-confirm">Confirmar Recusa</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProfessionalApprovals;
